import React, { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import { useGlobalContext } from "context/globalContext";
import { IoMdClose } from "react-icons/io";
import { createOrUpdatePromptVariablesAPI } from "Api/promptVariablesAPI";

const CreateOrUpdatePromptVariablesModal = ({
  isOpen,
  onClose,
  onSubmit,
  isEditMode,
}) => {
  const {
    promptVariablesFormData,
    setPromptVariablesFormData,
    initialPromptVariablesFormState,
    setFetchPromptVariables,
  } = useGlobalContext();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [hasChanged, setHasChanged] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      document.body.style.height = "100%";
      setHasChanged(false);
    } else {
      document.body.style.overflow = "";
      document.body.style.height = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.height = "";
    };
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedForm = {
      ...promptVariablesFormData,
      [name]: value,
    };
    setPromptVariablesFormData(updatedForm);

    if (name === "key") {
      setErrorMessage("");
    }

    const isChanged =
      updatedForm.name.trim() !== initialPromptVariablesFormState.name.trim();
    setHasChanged(isChanged);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid()) return;

    setIsSubmitting(true);
    const token = localStorage.getItem("jwtToken");
    let payload;

    if (promptVariablesFormData.id) {
      payload = {
        id: promptVariablesFormData.id,
        name: promptVariablesFormData.name,
      };
    } else {
      payload = { name: promptVariablesFormData.name };
    }

    const response = await createOrUpdatePromptVariablesAPI(
      payload,
      setIsSubmitting,
      token
    );

    if (response) {
      const successMessage = promptVariablesFormData?.id
        ? "Prompt variables updated successfully"
        : "Prompt variables created successfully";
      toast.success(successMessage);

      onSubmit(response.data);
      onClose();
      setPromptVariablesFormData(initialPromptVariablesFormState);
      setFetchPromptVariables(true);
    } else {
      toast.error(
        "Error creating/updating prompt variables. Please try again."
      );
    }
  };

  const handleCancel = () => {
    setPromptVariablesFormData(initialPromptVariablesFormState);
    onClose();
  };

  const isFormValid = useCallback(() => {
    const { name } = promptVariablesFormData;
    return Boolean(name?.trim());
  }, [promptVariablesFormData]);

  if (!isOpen) return null;

  return (
    <div className="bg-black fixed inset-0 z-50 flex h-screen items-center justify-center overscroll-contain bg-opacity-50">
      <div className="relative z-50 mx-auto flex max-h-[95dvh] w-2/5 flex-col items-center justify-start overflow-auto rounded-xl bg-white p-6 shadow-xl dark:!bg-gray-10 ">
        <button
                         type="button"
                         onClick={handleCancel}
                          className="absolute top-6 right-6 text-gray-500 hover:text-gray-600 focus:outline-none dark:hover:text-gray-100 "
                       >
                         <IoMdClose size={24} />
                       </button>
   

        <form onSubmit={handleSubmit} className="w-full">
          <div className="mt-4 flex justify-center gap-4">
            <div className="w-full">
              <label className="mb-2 block font-bold text-gray-700  dark:text-white">
                <span className="text-red-600">*</span> Name:
              </label>
              <input
                type="text"
                name="name"
                autoComplete="off"
                error={errorMessage}
                value={promptVariablesFormData?.name}
                onChange={handleChange}
                className="focus:border-transparent h-11 w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:!bg-gray-10 dark:text-white"
                required
              />
              {errorMessage && (
                <p className="mt-2 text-sm text-red-500">{errorMessage}</p>
              )}
            </div>
          </div>

          <div className="mt-3 flex justify-end space-x-4">
            <button
              type="submit"
              className={`rounded-md py-2 px-4 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 ${
                isSubmitting || !isFormValid() || !hasChanged
                  ? "cursor-not-allowed bg-gray-400"
                  : "bg-green-600 hover:bg-green-500"
              }`}
              disabled={isSubmitting || !isFormValid() || !hasChanged}
            >
              {isSubmitting ? "Submitting..." : isEditMode ? "Update" : "Add"}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="rounded-md bg-red-600 py-2 px-4 text-white hover:bg-red-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateOrUpdatePromptVariablesModal;
